import { createClient } from '@supabase/supabase-js';
import { Database } from '@/types/database.types';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

// Create the main Supabase client for authentication and general operations
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
  realtime: {
    params: {
      eventsPerSecond: 10,
    },
  },
  global: {
    headers: {
      'X-Client-Info': 'ingesta-app@1.0.0',
    },
  },
});

// Helper function to get the current user
export const getCurrentUser = async () => {
  const { data: { user }, error } = await supabase.auth.getUser();
  if (error) throw error;
  return user;
};

// Helper function to get the current session
export const getCurrentSession = async () => {
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error) throw error;
  return session;
};

// Auth event listeners
export const onAuthStateChange = (callback: (event: string, session: any) => void) => {
  return supabase.auth.onAuthStateChange(callback);
};

// Sign up with email and password
export const signUp = async (email: string, password: string, fullName?: string) => {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: {
        full_name: fullName,
        name: fullName,
      }
    }
  });
  
  if (error) throw error;
  return data;
};

// Sign in with email and password
export const signIn = async (email: string, password: string) => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });
  
  if (error) throw error;
  return data;
};

// Sign out
export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
};

// Password reset - request reset email
export const resetPassword = async (email: string, redirectTo?: string) => {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: redirectTo || `${window.location.origin}/reset-password`,
  });
  if (error) throw error;
};

// Sign in with OTP (magic link)
export const signInWithOtp = async (email: string) => {
  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: {
      emailRedirectTo: `${window.location.origin}/home`,
    },
  });
  if (error) throw error;
};

// Update user profile
export const updateProfile = async (updates: { email?: string; password?: string }) => {
  const { data, error } = await supabase.auth.updateUser(updates);
  if (error) throw error;
  return data;
};

// Helper to format auth errors for user display
export const formatAuthError = (error: any): string => {
  if (!error) return 'An unknown error occurred';
  
  // Handle Supabase auth errors
  if (error.message) {
    const message = error.message.toLowerCase();
    
    if (message.includes('invalid login credentials') || message.includes('invalid_credentials')) {
      return 'Invalid email or password. Please check your credentials and try again.';
    }
    
    if (message.includes('email not confirmed')) {
      return 'Please check your email and click the confirmation link before signing in.';
    }
    
    if (message.includes('email rate limit exceeded')) {
      return 'Too many requests. Please wait a few minutes before trying again.';
    }
    
    if (message.includes('password should be at least')) {
      return 'Password must be at least 6 characters long.';
    }
    
    if (message.includes('signup disabled')) {
      return 'New registrations are currently disabled. Please contact support.';
    }
    
    if (message.includes('user already registered')) {
      return 'An account with this email already exists. Please sign in instead.';
    }
    
    // Return the original message if no specific handling
    return error.message;
  }
  
  return 'An unexpected error occurred. Please try again.';
};

export default supabase;